/* The signed-in user's face: their Google photo, or their initial on the accent.

   Extracted when the mobile tab bar needed a third copy of what g-header and
   settings-account had each grown independently — identical apart from a size
   and a font-size, which is exactly the drift ADR 0003 decisions 11-13 had to
   clean up once already.

   Sized by `--avatar-size` rather than a property, matching g-icon's
   `--icon-size`: the caller is always styling a slot it owns, and a custom
   property inherits through the shadow boundary where an attribute would have to
   be threaded through every wrapper. */

import { LitElement, html, css } from 'lit';
import { shadowReset } from '../../design/base.js';
import { customElement, property } from 'lit/decorators.js';
import type { Me } from '../../core/types.js';

@customElement('g-avatar')
export class GAvatar extends LitElement {
  static override styles = [shadowReset, css`
    :host {
      display: grid;
      place-items: center;
      width: var(--avatar-size, 28px);
      height: var(--avatar-size, 28px);
      /* Scales the initial with the circle, so one token sizes both. */
      font-size: calc(var(--avatar-size, 28px) * 0.45);
      border-radius: var(--radius-full);
      background: var(--accent);
      color: var(--accent-ink);
      font-family: var(--font-ui);
      font-weight: 700;
      overflow: hidden;
      flex: none;
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `];

  @property({ attribute: false }) me: Me | null = null;

  /**
   * First character of the display name, falling back to the email.
   *
   * `?` rather than an empty circle when neither exists: signed-out is a state
   * worth seeing, and a blank accent dot reads as a rendering bug.
   */
  private initial(): string {
    const src = this.me?.display_name || this.me?.email || '?';
    return src.trim().charAt(0).toUpperCase();
  }

  override render() {
    /* alt="" because every call site labels the surrounding control — a tab, a
       row — and a second announcement of the same person is noise. */
    return this.me?.photo_url
      ? html`<img src=${this.me.photo_url} alt="" referrerpolicy="no-referrer" />`
      : html`${this.initial()}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-avatar': GAvatar;
  }
}
